import { Locale } from 'enums/locale';
import { NextRouter } from 'next-translate-routes';
import { localizationService } from 'services/LocalizationService';
import { NextParsedUrlQuery } from 'next/dist/server/request-meta';
import { shouldRemoveAllSeoLinks } from './paginationSeoUtils';

/**
 * Client-side check to determine if SEO links should be removed
 * This is a simplified version that works without server-side locale data
 */
function shouldRemoveSeoLinksClientSide(
  query: NextParsedUrlQuery,
  pathname: string
): boolean {
  const filteredQuery = removePathnameKeysFromQuery(pathname, query);

  // Common pagination parameter names across locales
  const paginationParams = ['page', 'sayfa'];

  // Remove pagination parameters from query
  const queryWithoutPagination = { ...filteredQuery };
  paginationParams.forEach((param) => {
    delete queryWithoutPagination[param];
  });

  // Check if there's a pagination parameter
  const hasPaginationParam = paginationParams.some(
    (param) => filteredQuery[param] !== undefined
  );

  // If there's pagination AND other query parameters, remove SEO links
  return hasPaginationParam && Object.keys(queryWithoutPagination).length > 0;
}

export function getAlternateHref(
  router: NextRouter,
  pageQueryParams: Record<Locale, string>
) {
  const locales = router.locales || [];
  const routerAsPath = router.asPath;
  const routerLocale = router.locale as Locale;
  const pathname = router.pathname;

  // Check if SEO links should be removed (client-side check)
  const shouldRemoveLinks = shouldRemoveSeoLinksClientSide(
    router.query,
    pathname
  );

  if (shouldRemoveLinks) {
    return [];
  }

  return locales
    .map((l) => {
      const locale = l as Locale;

      const translatedAsPath = getTranslatedAsPath({
        locale,
        pathname,
        routerLocale,
        routerAsPath,
      });

      let completeUrl = generateCompleteUrl({
        routerAsPath,
        translatedAsPath,
      });

      completeUrl = trimTrailingSlashIfExists(completeUrl);
      completeUrl = processSeoUrl(
        completeUrl,
        router.query,
        pageQueryParams,
        locale,
        pathname
      );

      const links = [
        <link
          key={completeUrl}
          rel="alternate"
          hrefLang={l}
          href={completeUrl}
        />,
      ];

      if (l === routerLocale) {
        links.push(
          <link
            key={`${completeUrl}-cannonical`}
            rel="canonical"
            href={completeUrl}
          />
        );
      }

      return links;
    })
    .flat();
}

/**
 * Async version of getAlternateHref that can check for noindex pages
 * and conditionally remove SEO links
 */
export async function getAlternateHrefWithSeoCheck(
  router: NextRouter,
  pageQueryParams: Record<Locale, string>,
  params?: NextParsedUrlQuery
): Promise<JSX.Element[]> {
  const locales = router.locales || [];
  const routerLocale = router.locale as Locale;

  // Check if SEO links should be removed for noindex pages with multiple query params
  const shouldRemoveLinks = await shouldRemoveAllSeoLinks(
    router.query,
    routerLocale,
    locales,
    params
  );

  if (shouldRemoveLinks) {
    return [];
  }

  // Use the existing logic if links should not be removed
  return getAlternateHref(router, pageQueryParams);
}

function getTranslatedAsPath({
  pathname,
  routerAsPath,
  locale,
  routerLocale,
}: {
  pathname: string;
  routerAsPath: string;
  locale: Locale;
  routerLocale: Locale;
}) {
  return localizationService.getTranslatedRouteWithSlugs(
    { pathname, asPath: routerAsPath, locale: routerLocale } as NextRouter,
    locale
  );
}

function generateCompleteUrl({
  translatedAsPath,
  routerAsPath,
}: {
  translatedAsPath: string;
  routerAsPath: string;
}) {
  return translatedAsPath
    ? `https://www.teknevia.com${translatedAsPath}`
    : `https://www.teknevia.com${routerAsPath}`;
}

function trimTrailingSlashIfExists(completeUrl: string) {
  return completeUrl.endsWith('/') ? completeUrl.slice(0, -1) : completeUrl;
}

function processSeoUrl(
  completeUrl: string,
  query: NextParsedUrlQuery,
  pageQueryParams: Record<Locale, string>,
  locale: Locale,
  pathname: string
) {
  const filteredQuery = removePathnameKeysFromQuery(pathname, query);

  const queryKeys = Object.keys(filteredQuery);

  if (queryKeys.length === 0) {
    return completeUrl;
  }

  const pageParamName = pageQueryParams[locale];

  const allPageParamNames = Object.values(pageQueryParams);
  const existingPageParam = allPageParamNames.find(
    (paramName) => filteredQuery[paramName] !== undefined
  );

  const pageValue = existingPageParam ? filteredQuery[existingPageParam] : null;
  const hasPaginationParam = pageValue !== null;

  const hasOnlyPaginationParam =
    queryKeys.length === 1 && allPageParamNames.includes(queryKeys[0]);

  if (hasOnlyPaginationParam && hasPaginationParam && pageParamName) {
    return `${completeUrl}?${pageParamName}=${pageValue}`;
  }

  return completeUrl.split('?')[0];
}

function removePathnameKeysFromQuery(
  pathname: string,
  query: NextParsedUrlQuery
): NextParsedUrlQuery {
  const pathnameKeys = pathname
    .split('/')
    .filter((segment) => segment.startsWith('[') && segment.endsWith(']'))
    .map((segment) => segment.slice(1, -1)); // Remove [ and ]

  const filteredQuery: NextParsedUrlQuery = {};

  Object.keys(query).forEach((key) => {
    if (!pathnameKeys.includes(key)) {
      filteredQuery[key] = query[key];
    }
  });

  return filteredQuery;
}
